import {
  API_PRINT_MAIL_BATCH,
  API_PRINT_MAIL_BATCHES,
  API_PRINT_MAIL_CONFIG,
  API_PRINT_MAIL_DISPATCH,
  API_PRINT_MAIL_PIECE_CANCEL,
  API_PRINT_MAIL_PIECE_DOCUMENT,
  API_PRINT_MAIL_PIECE_RELEASE,
  API_PRINT_MAIL_PIECES,
  API_PRINT_MAIL_QUOTE,
  API_PRINT_MAIL_STATUS,
  API_PRINT_MAIL_TAX_FORM_ELIGIBILITY,
  API_PRINT_MAIL_TAX_FORM_RUN,
  API_PRINT_MAIL_VERIFY_ADDRESS,
} from 'config'
import {
  IMailAddress,
  IMailPostageOverrides,
  IPrintMailConfig,
  IPrintMailPieceFilters,
  ITaxFormRunParams,
} from 'pages/Servicing/PrintMail/types'
import Api from 'services/api'

/** The saved configuration, plus the readiness the server works out from it and the credential. */
export const getPrintMailConfig = () => {
  return Api.get(API_PRINT_MAIL_CONFIG)
}

export const savePrintMailConfig = (data: Partial<IPrintMailConfig>) => {
  return Api.put(API_PRINT_MAIL_CONFIG, data)
}

/**
 * Whether anything can actually be posted, and what is missing if not.
 *
 * Read on its own as well as with the config, because `live` (a real postage-spending credential
 * rather than a sandbox key) has to be on screen anywhere a user can spend money.
 */
export const getPrintMailStatus = () => {
  return Api.get(API_PRINT_MAIL_STATUS)
}

/** What a piece of this shape would cost, before anyone commits to it. An estimate, always. */
export const getPrintMailQuote = (data: {
  purpose: string
  pageCount?: number
  pieces?: number
  overrides?: IMailPostageOverrides
}) => {
  return Api.post(API_PRINT_MAIL_QUOTE, data)
}

/** Standardise and score an address without posting anything. */
export const verifyPrintMailAddress = (data: { loanNumber?: number; address?: string }) => {
  return Api.post(API_PRINT_MAIL_VERIFY_ADDRESS, data)
}

/**
 * Queue one document to be printed and posted.
 *
 * `documentKey` is an S3 key the platform already holds: the piece has to carry the exact bytes
 * that were filed, so there is no upload here.
 */
export const queueMailPiece = (data: {
  loanNumber: number
  purpose: string
  documentKey: string
  documentName?: string
  to?: IMailAddress
  overrides?: IMailPostageOverrides
}) => {
  return Api.post(API_PRINT_MAIL_PIECES, data)
}

export const getMailPieces = (filters: IPrintMailPieceFilters) => {
  return Api.get(API_PRINT_MAIL_PIECES, filters)
}

/** A link to exactly what went in the envelope. Comes back as `{url}` or `{content}`. */
export const getMailPieceDocument = (id: string) => {
  return Api.get(API_PRINT_MAIL_PIECE_DOCUMENT, {}, { id })
}

/** Free while the piece is queued here; refused once the provider has printed it. */
export const cancelMailPiece = (id: string) => {
  return Api.post(API_PRINT_MAIL_PIECE_CANCEL, {}, { id })
}

/** Post a held piece anyway, or re-queue one whose address has since been corrected. */
export const releaseMailPiece = (id: string) => {
  return Api.post(API_PRINT_MAIL_PIECE_RELEASE, {}, { id })
}

/** Post what is queued, now, for a tenant who keeps auto-dispatch off deliberately. */
export const dispatchMailPieces = (limit?: number) => {
  return Api.post(API_PRINT_MAIL_DISPATCH, limit ? { limit } : {})
}

export const getMailBatches = (params: { kind?: string; limit?: number } = {}) => {
  return Api.get(API_PRINT_MAIL_BATCHES, params)
}

/** A batch plus the live counts of what its pieces have since done. */
export const getMailBatch = (id: string) => {
  return Api.get(API_PRINT_MAIL_BATCH, {}, { id })
}

/** Who qualifies for a 1098 this year, who cannot be posted one and why, and the total cost. */
export const getTaxFormMailEligibility = (data: ITaxFormRunParams) => {
  return Api.post(API_PRINT_MAIL_TAX_FORM_ELIGIBILITY, data)
}

/**
 * Start the year-end run. Returns `{runId, queuePosition}`; the batch is what reports progress.
 *
 * It posts forms that already exist and generates nothing, so a year whose forms have not been
 * generated under Servicing > Tax Forms has nothing for it to do.
 */
export const startTaxFormMailRun = (data: ITaxFormRunParams) => {
  return Api.post(API_PRINT_MAIL_TAX_FORM_RUN, data)
}
